import React, { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    Button,
    Typography,
    InputAdornment,
    CircularProgress,
    Stepper,
    Step,
    StepLabel,
} from '@mui/material';
import {
    Phone as PhoneIcon,
    Lock as LockIcon,
    Check as CheckIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import { loginStart, loginSuccess, loginFailure } from '../../store/slices/authSlice';
import { authService } from '../../services/authService';
import LoadingOverlay from '../../components/Shared/LoadingOverlay';

// Validation schemas
const phoneValidationSchema = yup.object({
    phone_number: yup
        .string()
        .required('Phone number is required')
        .matches(
            //   /^(?:\+91|91)?[6-9]\d{9}$/,
            /^[6-9]\d{9}$/,
            'Please enter a valid Indian phone number (10 digits)'
        ),
});

const otpValidationSchema = yup.object({
    otp: yup
        .string()
        .required('OTP is required')
        .length(4, 'OTP must be 4 digits')
        .matches(/^\d+$/, 'OTP must contain only numbers'),
});

const OTPLogin = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { isLoading, error } = useSelector((state) => state.auth);

    const [step, setStep] = useState(0); // 0: Phone entry, 1: OTP verification
    const [phoneNumber, setPhoneNumber] = useState('');
    const [otpSent, setOtpSent] = useState(false);
    const [timeLeft, setTimeLeft] = useState(0);
    const [resendCount, setResendCount] = useState(0);
    const [localError, setLocalError] = useState('');

    const from = location.state?.from?.pathname || '/dashboard';

    // Timer for OTP expiry
    useEffect(() => {
        let interval;
        if (timeLeft > 0) {
            interval = setInterval(() => {
                setTimeLeft((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [timeLeft, otpSent]);

    // Phone number form
    const phoneFormik = useFormik({
        initialValues: {
            phone_number: '',
        },
        validationSchema: phoneValidationSchema,
        onSubmit: async (values) => {
            try {
                dispatch(loginStart());
                setLocalError('');

                const response = await authService.sendOtp(values.phone_number, 'login');

                if (response.success) {
                    toast.success('OTP sent to your mobile number');
                    setPhoneNumber(values.phone_number);
                    setOtpSent(true);
                    setTimeLeft(300); // 5 minutes
                    setResendCount(0);
                    setStep(1);
                    dispatch(loginFailure('')); // Clear any previous errors
                } else {
                    setLocalError(response.error || 'Failed to send OTP');
                    dispatch(loginFailure(response.error || 'Failed to send OTP'));
                }
            } catch (err) {
                const message = err.response?.data?.error || 'Failed to send OTP';
                setLocalError(message);
                dispatch(loginFailure(message));
                toast.error(message);
            }
        },
    });

    // OTP verification form
    const otpFormik = useFormik({
        initialValues: {
            otp: '',
        },
        validationSchema: otpValidationSchema,
        onSubmit: async (values) => {
            try {
                dispatch(loginStart());
                setLocalError('');

                const response = await authService.verifyOtp(
                    phoneNumber,
                    values.otp,
                    'login'
                );

                if (response.success) {
                    toast.success('Login successful!');
                    dispatch(loginSuccess({
                        username: response.username,
                        full_name: response.full_name,
                        access_token: response.access_token,
                        refresh_token: response.refresh_token,
                        role: response.role,
                        id: response.id,
                        district: response.district,
                        subdivision: response.subdivision,
                        block: response.block,
                    }));
                    navigate(from, { replace: true });
                } else {
                    setLocalError(response.error || 'OTP verification failed');
                    dispatch(loginFailure(response.error || 'OTP verification failed'));
                }
            } catch (err) {
                const message = err.response?.data?.error || 'OTP verification failed';
                setLocalError(message);
                dispatch(loginFailure(message));
                toast.error(message);
            }
        },
    });

    const handleResendOtp = async () => {
        try {
            dispatch(loginStart());
            setLocalError('');

            const response = await authService.resendOtp(phoneNumber, 'text');

            if (response.success) {
                toast.success('OTP resent to your mobile number');
                setTimeLeft(300); // Reset to 5 minutes
                setResendCount((prev) => prev + 1);
                setOtpSent(true);
                dispatch(loginFailure('')); // Clear any previous errors
            } else {
                setLocalError(response.error || 'Failed to resend OTP');
                toast.error(response.error || 'Failed to resend OTP');
            }
        } catch (err) {
            const message = err.response?.data?.error || 'Failed to resend OTP';
            setLocalError(message);
            toast.error(message);
        }
    };

    const handleBackToPhone = () => {
        setStep(0);
        setOtpSent(false);
        setTimeLeft(0);
        otpFormik.resetForm();
        phoneFormik.resetForm();
        setLocalError('');
    };

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${minutes}:${secs < 10 ? '0' : ''}${secs}`;
    };

    const isOtpExpired = otpSent && timeLeft === 0;

    return (
        <Box
            sx={{
                textAlign: 'center',
            }}
        >
            {/* Stepper */}
            <Stepper activeStep={step} sx={{ mb: 3 }} alternativeLabel>
                <Step>
                    <StepLabel>Enter Phone No.</StepLabel>
                </Step>
                <Step>
                    <StepLabel>Verify OTP</StepLabel>
                </Step>
            </Stepper>

            {/* {(error || localError) && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error || localError}
              </Alert>
            )} */}

            {/* Step 0: Phone Number Entry */}
            {step === 0 && (
                <Box component="form" onSubmit={phoneFormik.handleSubmit}>
                    <TextField
                        fullWidth
                        id="phone_number"
                        name="phone_number"
                        label="Phone Number"
                        placeholder="9876543210"
                        value={phoneFormik.values.phone_number}
                        onChange={phoneFormik.handleChange}
                        onBlur={phoneFormik.handleBlur}
                        error={phoneFormik.touched.phone_number && Boolean(phoneFormik.errors.phone_number)}
                        helperText={phoneFormik.touched.phone_number && phoneFormik.errors.phone_number}
                        margin="normal"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <PhoneIcon color="action" />
                                    <Typography variant="body2" sx={{ ml: 0.5 }}>
                                        +91
                                    </Typography>
                                </InputAdornment>
                            ),
                        }}
                        disabled={isLoading}
                    />

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{ mt: 3, mb: 2, py: 1.5 }}
                        disabled={isLoading || !phoneFormik.isValid}
                    >
                        {isLoading ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            'Send OTP'
                        )}
                    </Button>
                </Box>
            )}

            {/* Step 1: OTP Verification */}
            {step === 1 && (
                <Box component="form" onSubmit={otpFormik.handleSubmit}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        OTP has been sent to <strong>+91 {phoneNumber}</strong>
                    </Typography>

                    <TextField
                        fullWidth
                        id="otp"
                        name="otp"
                        label="Enter OTP"
                        placeholder="0000"
                        value={otpFormik.values.otp}
                        onChange={otpFormik.handleChange}
                        onBlur={otpFormik.handleBlur}
                        error={otpFormik.touched.otp && Boolean(otpFormik.errors.otp)}
                        helperText={otpFormik.touched.otp && otpFormik.errors.otp}
                        margin="normal"
                        inputProps={{
                            maxLength: 4,
                            inputMode: 'numeric',
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <LockIcon color="action" />
                                </InputAdornment>
                            ),
                        }}
                        disabled={isLoading}
                    />

                    {/* Timer and Resend */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mt: 2,
                            mb: 3,
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                color: isOtpExpired ? '#d32f2f' : '#666',
                                fontWeight: isOtpExpired ? 'bold' : 'normal',
                            }}
                        >
                            {isOtpExpired
                                ? 'OTP Expired'
                                : `Time left: ${formatTime(timeLeft)}`}
                        </Typography>

                        {resendCount < 3 ? (
                            <Button
                                size="small"
                                onClick={handleResendOtp}
                                disabled={isLoading || (!isOtpExpired && timeLeft > 180)}
                                sx={{ textTransform: 'none' }}
                            >
                                Resend OTP {resendCount > 0 ? `(${resendCount}/3)` : ''}
                            </Button>
                        ) : (
                            <Typography variant="caption" color="error">
                                Max resend attempts reached
                            </Typography>
                        )}
                    </Box>

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{ mt: 2, mb: 2, py: 1.5 }}
                        disabled={isLoading || !otpFormik.isValid || isOtpExpired}
                        startIcon={<CheckIcon />}
                    >
                        {isLoading ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            'Verify OTP & Login'
                        )}
                    </Button>

                    <Button
                        fullWidth
                        variant="text"
                        sx={{ py: 1.5 }}
                        onClick={handleBackToPhone}
                        disabled={isLoading}
                    >
                        Change Phone Number
                    </Button>
                </Box>
            )}

            {/* Loading Overlay */}
            <LoadingOverlay open={isLoading} message="Processing..." />
        </Box>
    );
};

export default OTPLogin;
